import { apiFetch, showAppState } from "./app-state.jsx";
import React from "react";
import "./login.css";
import logo from "./assets/hallef-logo-tight.png";

function Logo() {
  return (
    <a
      className="login-logo"
      href="/"
      aria-label="Hallef Technologies home"
    >
      <img src={logo} alt="Hallef Technologies" />
    </a>
  );
}

function getApiUrl() {
  return (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/+$/, "");
}

function AuthPage({ mode }) {
  const isRegister = mode === "register";

  const [form, setForm] = React.useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  function updateForm(event) {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));

    setMessage("");
  }

  async function submitAuth(event) {
  event.preventDefault();

  if (!form.email.trim() || !form.password) {
    showAppState("form-validation", { errors: { credentials: "Enter email and password." } });
    setMessage("Enter email and password.");
    return;
  }

  if (isRegister && form.password.length < 8) {
    showAppState("form-validation", { errors: { password: "Password must be at least 8 characters." } });
    setMessage("Password must be at least 8 characters.");
    return;
  }

  setLoading(true);
  setMessage("");

  try {
    const apiUrl = getApiUrl();
    const endpoint = isRegister ? "register" : "login";

    const response = await apiFetch(`${apiUrl}/auth/${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: form.email.trim(),
        password: form.password,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      setMessage(
        response.status === 401
          ? "Invalid admin credentials."
          : response.status === 409
          ? "This email is already registered."
          : data.message || "Login failed."
      );
      return;
    }

    const role = (data.admin?.role || "user").trim().toLowerCase();

    localStorage.setItem(
      "hallef-admin-token",
      data.accessToken
    );

    localStorage.setItem(
      "hallef-admin-auth",
      role === "admin" ? "true" : "false"
    );

    localStorage.setItem(
      "hallef-user-role",
      role
    );

    if (role === "admin") window.location.href = "/admin";
    else showAppState("success", { message: "You signed in successfully." });
  } catch (error) {
    console.error("LOGIN ERROR:", error);
    setMessage("Cannot connect to the backend server.");
  } finally {
    setLoading(false);
  }
  }

  return (
    <main className="login-page">
      <section
        className="login-visual"
        aria-label="Admin login artwork"
      >
        <Logo />

        <div className="login-visual-copy">
          <p>{isRegister ? "Create Access" : "Admin Panel"}</p>

          <h1>
            {isRegister
              ? "Create an account for Hallef access."
              : "Manage Hallef website content from one clean workspace."}
          </h1>
        </div>

        <div
          className="login-stats"
          aria-hidden="true"
        >
          <span>Services</span>
          <span>Careers</span>
          <span>Inquiries</span>
        </div>
      </section>

      <section
        className="login-panel"
        aria-label={isRegister ? "Register form" : "Admin login form"}
      >
        <div className="login-card">
          <p className="login-kicker">
            {isRegister ? "New Account" : "Secure Access"}
          </p>

          <h2>{isRegister ? "Register" : "Admin Login"}</h2>

          <p className="login-note">
            {isRegister
              ? "Create a user account. Admin access can be enabled manually later."
              : "Use your admin credentials to continue."}
          </p>

          <form onSubmit={submitAuth}>
            <label>
              <span>Email address <span className="required-star">*</span></span>

              <input
                autoComplete="email"
                name="email"
                onChange={updateForm}
                placeholder={
                  isRegister
                    ? "you@example.com"
                    : "admin@hallef.tech"
                }
                type="email"
                value={form.email}
              />
            </label>

            <label>
              <span>Password <span className="required-star">*</span></span>

              <div className="password-field">
                <input
                  autoComplete={isRegister ? "new-password" : "current-password"}
                  name="password"
                  onChange={updateForm}
                  placeholder={
                    isRegister
                      ? "Create password"
                      : "Enter password"
                  }
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  value={form.password}
                />

                <button
                  className="password-toggle"
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  title={showPassword ? "Hide password" : "Show password"}
                  onClick={() =>
                    setShowPassword(
                      (visible) => !visible
                    )
                  }
                >
                  <span aria-hidden="true" />
                </button>
              </div>
            </label>

            {message && (
              <p
                className="login-error"
                role="alert"
              >
                {message}
              </p>
            )}

            <button
              className="login-submit"
              type="submit"
              disabled={loading}
            >
              {loading
                ? isRegister
                  ? "Creating account..."
                  : "Logging in..."
                : isRegister
                ? "Register"
                : "Login"}
            </button>
          </form>

          <p className="auth-switch">
            {isRegister
              ? "Already have an account?"
              : "Need an account?"}{" "}
            <a href={isRegister ? "/login" : "/register"}>
              {isRegister ? "Login" : "Register"}
            </a>
          </p>
        </div>
      </section>
    </main>
  );
}

function LoginPage() {
  return <AuthPage mode="login" />;
}

export function RegisterPage() {
  return <AuthPage mode="register" />;
}

export default LoginPage;
